import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const RitualGuard = ({ children }) => {
  const { checkDailyRitualStatus, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [ritualChecked, setRitualChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastCheckPath, setLastCheckPath] = useState('');
  const [lastCheckDate, setLastCheckDate] = useState(''); // Track last check date

  // Temporary bypass - check if URL has bypass parameter
  const urlParams = new URLSearchParams(window.location.search);
  const bypassRitual = urlParams.get('bypass') === 'true';

  useEffect(() => {
    const performRitualCheck = async () => {
      // Reset loading and checked states on every mount
      setRitualChecked(false);
      setLastCheckDate('');
      
      // Bypass ritual check if URL parameter is set
      if (bypassRitual) {
        console.log('🔓 Ritual bypass enabled - skipping ritual check');
        setLoading(false);
        setRitualChecked(true);
        return;
      }

      // Only check ritual for authenticated users
      if (!isAuthenticated || !token) {
        setLoading(false);
        setRitualChecked(true);
        return;
      }

      // Don't check ritual on ritual/auth pages or admin routes (admins must not be trapped)
      const exemptPaths = ['/login', '/register', '/'];
      if (
        exemptPaths.includes(location.pathname) ||
        location.pathname.startsWith('/daily-ritual') ||
        location.pathname.startsWith('/protocol-complete') ||
        location.pathname.startsWith('/admin')
      ) {
        setLoading(false);
        setRitualChecked(true);
        return;
      }

      try {
        console.log('🔍 Checking daily ritual status...');
        const ritualCompleted = await checkDailyRitualStatus();
        
        console.log('🎯 Ritual status:', { 
          completed: ritualCompleted, 
          currentPath: location.pathname 
        });

        if (!ritualCompleted) {
          console.log('🚨 Ritual not completed - redirecting to daily ritual');
          navigate('/daily-ritual');
          return;
        } else {
          console.log('✅ Ritual completed - allowing normal navigation');
        }

        setLastCheckPath(location.pathname);
        setLastCheckDate(new Date().toDateString());
      } catch (error) {
        console.error('❌ Error checking ritual status:', error);
        console.log('⚠️ Ritual check failed - allowing navigation to prevent loop');
      }

      setRitualChecked(true);
      setLoading(false);
    };

    performRitualCheck();
  }, [isAuthenticated, token, location.pathname]); // FIXED: Removed problematic dependencies

  // Show loading state while checking ritual
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-300 border-t-emerald-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-emerald-700 font-medium">Checking your daily ritual status...</p>
        </div>
      </div>
    );
  }

  return children;
};

export default RitualGuard;